import { useCallback } from 'react';
import { CREATE_WEDDING_APP_NAME } from '@apps/createWedding/constants';
import { useHistory, useIdentify, useTranslation } from '@shared/core';
import { useCreateWeddingTelemetry } from '@apps/createWedding/CreateWedding.telemetry';
import { useAuth } from '@shared/components/AuthProvider';
import { useEventState } from '@apps/createWedding/EventState';
import queryString from 'query-string';
import { useNativeState } from '@shared/core/native';
import { dispatchWeddingCreationResultsToNativeApps } from '@shared/core/native/utils';
import { SurveyItems } from '@apps/createWedding/routes/Survey/Survey.types';

const SURVEY_OPTION_IDS = Object.values(SurveyItems);

export const useSurveySkip = () => {
  const { addEventCreatedListener } = useEventState();
  const history = useHistory();
  const { tq } = useTranslation('createWedding');
  const telemetry = useCreateWeddingTelemetry();
  const { identify } = useIdentify();
  const { currentUser } = useAuth();
  const { isNative } = useNativeState();

  const sendMessageToNativeApps = useCallback(
    (eventId?: string) => {
      if (isNative) {
        if (!eventId) {
          telemetry.trackError('SurveyMessageToNativeApps', 'Message send attempt to native apps failed; eventId does not exist.');
          return;
        }

        dispatchWeddingCreationResultsToNativeApps(eventId, SURVEY_OPTION_IDS);
      }
    },
    [isNative, telemetry],
  );

  const proceedFurther = useCallback(() => {
    const queryParams = queryString.stringify({ onboardingInterests: SURVEY_OPTION_IDS });
    return history.push(`/${CREATE_WEDDING_APP_NAME}/tying-the-knot?${queryParams}`);
  }, [history]);

  const onSurveySkip = useCallback(() => {
    identify({ initialInterests: [] });
    // async required by EventListenerCallback type signature
    // No await needed in skip flow - unlike proceed flow, we don't set up pages/themes
    addEventCreatedListener(async event => {
      sendMessageToNativeApps(event.createEventV0301?.id);
      telemetry.surveySubmitClicked({
        action: 'SurveySkip',
        label: tq`Skip`,
        selectedOptions: SURVEY_OPTION_IDS,
        email: currentUser?.profile?.email,
        eventId: event.createEventV0301?.eventId,
      });
    });

    proceedFurther();
  }, [identify, addEventCreatedListener, sendMessageToNativeApps, telemetry, currentUser, proceedFurther, tq]);

  return { onSurveySkip };
};
